import type { SupabaseClient } from "@supabase/supabase-js";
import { ownerScopedPath, type StoragePath } from "@/lib/storage/paths";

/**
 * THE `avatars` STORE — the only module that names this bucket.
 *
 * `.claude/rules/storage.md`. Isomorphic and client-injected like the
 * deal-documents store; avatars are uploaded from the browser with the user's
 * own session.
 *
 * Write ownership is enforced by the restrictive avatar_owner_* bucket
 * policies: the first path segment must equal auth.uid(). Public SELECT is
 * unchanged. The real upload/overwrite/delete controls live in
 * storage-scoping.integration.test.ts; a path builder alone is not authority.
 *
 * Changing the shape is safe: `users.avatar_url` stores a full public URL,
 * nothing in the app parses it (it is only ever an `<img src>`), the SELECT
 * policy is path-independent, and existing objects keep resolving at their old
 * URLs.
 */

export const AVATARS_BUCKET = "avatars";

export type AvatarPath = StoragePath<typeof AVATARS_BUCKET>;

/** `<userId>/<fileName>` — the bucket policy enforces the owner segment. */
export function ownerAvatarPath(ownerId: string, fileName: string): AvatarPath {
  return ownerScopedPath<typeof AVATARS_BUCKET>(ownerId, fileName);
}

export class AvatarStorageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AvatarStorageError";
  }
}

export const AvatarStore = {
  /** Put the image bytes and return the public URL to persist on the user row. */
  async upload(
    supabase: SupabaseClient,
    path: AvatarPath,
    body: Blob | File | ArrayBuffer | Uint8Array,
    options: { contentType: string; upsert?: boolean }
  ): Promise<string> {
    const { error } = await supabase.storage.from(AVATARS_BUCKET).upload(path, body, {
      // Required for the same reason as in the deal-documents store: this
      // bucket sets `allowed_mime_types`, so the library's raw-body default
      // is rejected and an explicit `undefined` is rejected as malformed.
      contentType: options.contentType,
      upsert: options.upsert ?? true,
    });
    if (error) {
      throw new AvatarStorageError(`Failed to upload avatar: ${error.message}`);
    }
    return AvatarStore.publicUrl(supabase, path);
  },

  /**
   * The permanent public URL for an avatar object. The bucket is public, so
   * this is a plain URL construction with no signing and no expiry.
   */
  publicUrl(supabase: SupabaseClient, path: AvatarPath): string {
    const { data } = supabase.storage.from(AVATARS_BUCKET).getPublicUrl(path);
    return data.publicUrl;
  },
};

/**
 * Is this URL an avatar object in OUR bucket, belonging to `ownerId`?
 *
 * Lives HERE because this module owns the bucket and the path convention, so
 * the check moves with them; the route that validates `avatar_url` on the way
 * into `users` cannot drift from `ownerAvatarPath`.
 *
 * Checks the ORIGIN, not a substring — the same lesson as
 * `isOwnSignedDealDocumentUrl`: any host can serve a path that merely *looks*
 * like ours, and `avatar_url` is rendered as an `<img src>` on the admin users
 * pages, so an arbitrary URL is a stored tracking pixel that fires for every
 * admin who opens the list.
 *
 * And it pins the OWNER SEGMENT. Checking only the bucket prefix would accept
 * another user's avatar object as your own — harmless-looking, since the bucket
 * is public, but it lets one account point its profile at another's image and
 * makes `users.avatar_url` stop meaning "this user's upload".
 */
export function isOwnAvatarUrl(url: string, ownerId: string): boolean {
  const base = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!base) return false; // fail CLOSED — an unset env must not admit any URL
  let parsed: URL;
  let baseUrl: URL;
  try {
    parsed = new URL(url);
    baseUrl = new URL(base);
  } catch {
    return false;
  }
  if (parsed.origin !== baseUrl.origin) return false;
  // `ownerAvatarPath` produces `<ownerId>/<file>`, so the object path is
  // `/storage/v1/object/public/avatars/<ownerId>/…`. Derived from the builder
  // rather than restated, so a change to either moves both.
  const expected = `/storage/v1/object/public/${AVATARS_BUCKET}/${ownerId}/`;
  return parsed.pathname.startsWith(expected);
}
